import { useCallback, useEffect, useState } from "react";
import Joyride, { type CallBackProps, type Step } from "react-joyride";
import {
  Addwalkthroughstepprogress,
  getwalkthroughsteps,
  getWalkthroughStepProgress,
} from "../services/dashboard";

type WalkthroughStep = {
  id: number;
  parentId: number | null;
  stepOrder: number;
  highlightedElement?: string;
  tooltipContent?: string;
  name?: string;
  tooltipPlacement?: string;
};

type WalkthroughProgress = {
  stepId: number;
  isCompleted?: boolean;
  isSkipped?: boolean;
};

const joyrideStyles = {
  options: {
    primaryColor: "#005EB8",
    zIndex: 10000,
  },
  tooltipContainer: {
    borderRadius: 8,
    padding: 12,
  },
  buttonNext: {
    backgroundColor: "#005EB8",
    fontSize: 14,
    fontWeight: "bold" as const,
  },
  buttonBack: {
    color: "#005EB8",
    fontSize: 14,
  },
};

function getSessionKey(userId: number, parentId: number) {
  return `walkthrough-session-${userId}-${parentId}`;
}

export function clearWalkthroughSessionKeys() {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith("walkthrough-session-")) {
      sessionStorage.removeItem(key);
    }
  });
}

function isStepAddressed(progress: WalkthroughProgress) {
  return Boolean(progress.isCompleted || progress.isSkipped);
}

function isWalkthroughFinished(
  steps: WalkthroughStep[],
  progress: WalkthroughProgress[]
) {
  if (!steps.length) {
    return true;
  }

  const stepIds = steps.map((step) => step.id);
  const addressedIds = progress
    .filter((item) => isStepAddressed(item) && stepIds.includes(item.stepId))
    .map((item) => item.stepId);

  return stepIds.every((id) => addressedIds.includes(id));
}

function getNextIncompleteStepIndex(
  steps: WalkthroughStep[],
  progress: WalkthroughProgress[]
) {
  if (!steps.length) {
    return 0;
  }

  const addressedIds = progress
    .filter(isStepAddressed)
    .map((item) => item.stepId);

  const nextStep = steps.find((step) => !addressedIds.includes(step.id));
  if (!nextStep) {
    return 0;
  }

  return steps.findIndex((step) => step.id === nextStep.id);
}

function convertStepsToJoyride(
  steps: WalkthroughStep[],
  targetMap?: Record<string, string>
): Step[] {
  return [...steps]
    .sort((a, b) => a.stepOrder - b.stepOrder)
    .map((step) => {
      const mappedTarget =
        step.highlightedElement && targetMap?.[step.highlightedElement];

      return {
        target: mappedTarget ?? step.highlightedElement ?? "body",
        content: step.tooltipContent || step.name || "",
        placement:
          step.tooltipPlacement === "above"
            ? "top"
            : step.tooltipPlacement === "below"
              ? "bottom"
              : step.tooltipPlacement === "left"
                ? "left"
                : step.tooltipPlacement === "right"
                  ? "right"
                  : "bottom",
        disableBeacon: true,
      };
    });
}

async function markStepsSkipped(
  userId: number,
  steps: WalkthroughStep[],
  fromIndex: number,
  existingProgress: WalkthroughProgress[]
) {
  const stepsToSkip = steps.slice(fromIndex);

  for (const step of stepsToSkip) {
    if (!existingProgress.some((item) => item.stepId === step.id)) {
      await Addwalkthroughstepprogress({
        userId,
        stepId: step.id,
        isCompleted: false,
        isSkipped: true,
        completionPercentage: 0,
      });
    }
  }
}

interface UsePageWalkthroughOptions {
  userId?: number | null;
  parentId: number;
  targetMap?: Record<string, string>;
}

export function usePageWalkthrough({
  userId,
  parentId,
  targetMap,
}: UsePageWalkthroughOptions) {
  const [pageSteps, setPageSteps] = useState<WalkthroughStep[]>([]);
  const [userProgress, setUserProgress] = useState<WalkthroughProgress[]>([]);
  const [joyrideRun, setJoyrideRun] = useState(false);
  const [joyrideSteps, setJoyrideSteps] = useState<Step[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const dismissForSession = useCallback(() => {
    if (!userId) return;
    sessionStorage.setItem(getSessionKey(userId, parentId), "dismissed");
  }, [parentId, userId]);

  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { action, index, status, type } = data;

      try {
        if (action === "close") {
          document.body.classList.remove("joyride-disabled-scroll");
          if (userId) {
            await markStepsSkipped(userId, pageSteps, currentStepIndex, userProgress);
            setUserProgress((prev) => {
              const next = [...prev];
              pageSteps.slice(currentStepIndex).forEach((step) => {
                if (!next.some((item) => item.stepId === step.id)) {
                  next.push({ stepId: step.id, isCompleted: false, isSkipped: true });
                }
              });
              return next;
            });
          }
          dismissForSession();
          setJoyrideRun(false);
          setCurrentStepIndex(0);
          return;
        }

        if (type === "step:after" && action === "next" && userId) {
          setCurrentStepIndex(index + 1);
          const currentStep = pageSteps[index];
          if (currentStep?.id) {
            const progressData = {
              userId,
              stepId: currentStep.id,
              isCompleted: true,
              isSkipped: false,
              completionPercentage: Math.round(((index + 1) / pageSteps.length) * 100),
            };
            await Addwalkthroughstepprogress(progressData);
            setUserProgress((prev) => [
              ...prev.filter((item) => item.stepId !== currentStep.id),
              { stepId: currentStep.id, isCompleted: true, isSkipped: false },
            ]);
          }
          return;
        }

        if (type === "step:after" && action === "prev") {
          setCurrentStepIndex(index - 1);
          return;
        }

        if (status === "finished" || status === "skipped") {
          if (status === "skipped" && userId) {
            await markStepsSkipped(userId, pageSteps, currentStepIndex, userProgress);
            setUserProgress((prev) => {
              const next = [...prev];
              pageSteps.slice(currentStepIndex).forEach((step) => {
                if (!next.some((item) => item.stepId === step.id)) {
                  next.push({ stepId: step.id, isCompleted: false, isSkipped: true });
                }
              });
              return next;
            });
          }
          dismissForSession();
          document.body.classList.remove("joyride-disabled-scroll");
          setJoyrideRun(false);
        }
      } catch (error) {
        console.error("Error saving walkthrough progress:", error);
      }
    },
    [currentStepIndex, dismissForSession, pageSteps, userId, userProgress]
  );

  useEffect(() => {
    if (!userId) return;

    const initializeWalkthrough = async () => {
      try {
        const sessionKey = getSessionKey(userId, parentId);
        if (sessionStorage.getItem(sessionKey)) {
          return;
        }

        const allSteps = (await getwalkthroughsteps()) as WalkthroughStep[];
        const scopedSteps = allSteps.filter((step) => step.parentId === parentId);
        if (!scopedSteps.length) {
          return;
        }

        setPageSteps(scopedSteps);
        setJoyrideSteps(convertStepsToJoyride(scopedSteps, targetMap));

        const progRes = await getWalkthroughStepProgress(userId);
        const progressData =
          progRes.succeeded && progRes.data
            ? Array.isArray(progRes.data)
              ? progRes.data
              : [progRes.data]
            : [];

        setUserProgress(progressData);

        if (isWalkthroughFinished(scopedSteps, progressData)) {
          sessionStorage.setItem(sessionKey, "completed");
          return;
        }

        const startIndex = getNextIncompleteStepIndex(scopedSteps, progressData);
        setCurrentStepIndex(startIndex);
        sessionStorage.setItem(sessionKey, "shown");
        document.body.classList.add("joyride-disabled-scroll");
        setTimeout(() => setJoyrideRun(true), 300);
      } catch (error) {
        console.error("Error initializing walkthrough:", error);
      }
    };

    initializeWalkthrough();
  }, [parentId, targetMap, userId]);

  const walkthrough = (
    <Joyride
      steps={joyrideSteps}
      run={joyrideRun}
      stepIndex={currentStepIndex}
      continuous
      showSkipButton
      showProgress
      disableScrolling={false}
      spotlightPadding={8}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
    />
  );

  return { walkthrough };
}
